import * as TWEEN from '@tweenjs/tween.js';
import OLMap from 'ol/Map';
import { fromLonLat } from 'ol/proj';
import { Extent, createEmpty as createEmptyExtent, extend, buffer } from 'ol/extent';

interface CameraOptions {
  center?: {
    lat: number;
    lng: number;
  };
  zoom?: number;
}

/**
 * Unified smooth camera movement function that works with both Google Maps and OpenLayers
 * @param googleMap Optional Google Maps instance
 * @param openLayersMap Optional OpenLayers Map instance
 * @param options Camera options containing center and/or zoom
 */
export const smoothMoveCamera = (
  googleMap: google.maps.Map | null,
  openLayersMap: OLMap | null,
  options: CameraOptions
) => {
  if (googleMap) {
    const currentCenter = googleMap.getCenter();
    const currentZoom = googleMap.getZoom();

    const cameraOptions = {
      center: {
        lat: currentCenter?.lat() ?? 0,
        lng: currentCenter?.lng() ?? 0,
      },
      zoom: currentZoom ?? 0,
    };

    const tween = new TWEEN.Tween(cameraOptions)
      .to(
        {
          center: options.center
            ? {
                lat: options.center.lat,
                lng: options.center.lng,
              }
            : {
                lat: currentCenter?.lat() ?? 0,
                lng: currentCenter?.lng() ?? 0,
              },
          zoom: options.zoom ? options.zoom : currentZoom,
        },
        400
      )
      .easing(TWEEN.Easing.Quadratic.Out) // Use an easing function to make the animation smooth.
      .onUpdate(() => {
        googleMap?.moveCamera({
          center: new google.maps.LatLng(cameraOptions.center.lat, cameraOptions.center.lng),
          zoom: cameraOptions.zoom,
        });
      })
      .start();

    const animate = (time: number) => {
      requestAnimationFrame(animate);
      tween.update(time);
    };

    requestAnimationFrame(animate);
  } else if (openLayersMap) {
    // OpenLayers implementation
    const view = openLayersMap.getView();
    const duration = 400;

    if (options.center) {
      const center = fromLonLat([options.center.lng, options.center.lat]);
      view.animate({
        center,
        duration,
        easing: (t) => t * (2 - t), // Quadratic out easing
      });
    }

    if (options.zoom !== undefined) {
      view.animate({
        zoom: options.zoom,
        duration,
        easing: (t) => t * (2 - t),
      });
    }
  }
};

/**
 * Calculate the extent (bounds) that contains all the given coordinates for OpenLayers
 */
const calculateOLExtent = (coordinates: Array<[number, number]>): Extent => {
  const extent = createEmptyExtent();
  coordinates.forEach(([lat, lng]) => {
    const point = fromLonLat([lng, lat]);
    extend(extent, point);
  });
  return buffer(extent, 100); // 100 meters padding
};

/**
 * Unified function to fit coordinates in view that works with both Google Maps and OpenLayers
 * @param googleMap Optional Google Maps instance
 * @param openLayersMap Optional OpenLayers Map instance
 * @param coordinates Array of [latitude, longitude] pairs
 * @param defaultZoom Fallback zoom level if coordinates array is empty or contains single point
 */
export const smoothFitCoordinates = (
  googleMap: google.maps.Map | null,
  openLayersMap: OLMap | null,
  coordinates: Array<[number, number]>,
  defaultZoom: number = 15
) => {
  if (coordinates.length === 0) return;

  if (coordinates.length === 1) {
    // Single point - center on it
    smoothMoveCamera(googleMap, openLayersMap, {
      center: {
        lat: coordinates[0][0],
        lng: coordinates[0][1],
      },
      zoom: defaultZoom,
    });
    return;
  }

  if (googleMap) {
    // Google Maps implementation
    const bounds = new google.maps.LatLngBounds();
    coordinates.forEach(([lat, lng]) => {
      bounds.extend(new google.maps.LatLng(lat, lng));
    });
    const center = bounds.getCenter();

    smoothMoveCamera(googleMap, null, {
      center: { lat: center.lat(), lng: center.lng() },
      zoom: defaultZoom,
    });
  } else if (openLayersMap) {
    // OpenLayers implementation
    const view = openLayersMap.getView();
    const extent = calculateOLExtent(coordinates);

    view.fit(extent, {
      duration: 400,
      easing: (t) => t * (2 - t),
      padding: [50, 50, 50, 50],
    });
  }
};
